// ==== FILE: src/utils/formatting.ts ====

import { currency as englishCurrencies } from '../translations/en.json';
import { currency as arabicCurrencies } from '../translations/ar.json';

// Listing grids and filter options repeatedly use the same locale. Constructing
// an Intl formatter for every value adds synchronous work to each interaction.
const numberFormatters = new Map<string, Intl.NumberFormat>();
const getNumberFormatter = (language: string): Intl.NumberFormat => {
  const cached = numberFormatters.get(language);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat(language, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    numberingSystem: 'latn',
  });
  if (numberFormatters.size >= 8) {
    numberFormatters.delete(numberFormatters.keys().next().value!);
  }
  numberFormatters.set(language, formatter);
  return formatter;
};

// Function to format currency using Latin digits, NO CENTS, and localized currency words
export const formatCurrency = (
  amount: number,
  language: string = 'en',
  currency: string = 'LYD' // Default currency to LYD
) => {
  // 1. Format the NUMBER part using the requested language for correct comma separators,
  // but strictly FORCE Latin digits (0-9) using numberingSystem: 'latn'.
  // We force 0 fraction digits to remove cents.
  const formattedNumber = getNumberFormatter(language).format(amount);

  // 2. Fetch the localized currency word (e.g., "دولار", "د.ل", "يورو")
  const currencies: Record<string, string> = language.startsWith('ar')
    ? arabicCurrencies
    : englishCurrencies;
  const currencyLabel = Object.prototype.hasOwnProperty.call(
    currencies,
    currency
  )
    ? currencies[currency]
    : currency;

  // 3. Return the combined string (Number + Currency Word)
  return `${formattedNumber} ${currencyLabel}`;
};

// Function to format numbers based on language (always using Latin digits)
export const formatNumber = (number: number, language: string = 'en') => {
  try {
    return getNumberFormatter(language).format(number);
  } catch (e) {
    console.error(
      `Error formatting number with Intl.NumberFormat (lang: ${language}):`,
      e
    );
    return String(number); // Fallback
  }
};

// Function to format dates based on language
export const formatDate = (
  date: Date | string,
  formatStyle: 'short' | 'medium' | 'long' = 'medium', // More specific types for style
  language: string = 'en'
) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) {
    console.warn(`Invalid date provided to formatDate: ${date}`);
    return String(date); // Return original string or an error indicator
  }

  const options: Intl.DateTimeFormatOptions = {};

  if (formatStyle === 'short') {
    options.day = '2-digit'; // Ensure two digits for day
    options.month = '2-digit'; // Ensure two digits for month
    options.year = 'numeric';
  } else if (formatStyle === 'medium') {
    options.day = 'numeric';
    options.month = 'short'; // e.g., 'May', 'أيار'
    options.year = 'numeric';
  } else if (formatStyle === 'long') {
    options.day = 'numeric';
    options.month = 'long'; // e.g., 'May', 'مايو'
    options.year = 'numeric';
    options.weekday = 'short'; // e.g., 'Thu', 'خميس' (or 'long')
  }

  try {
    return new Intl.DateTimeFormat(language, options).format(dateObj);
  } catch (e) {
    console.error(
      `Error formatting date with Intl.DateTimeFormat (lang: ${language}, style: ${formatStyle}):`,
      e
    );
    // Fallback to a simpler, universal format if Intl fails for some reason with specific locale/options
    return `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${dateObj.getDate().toString().padStart(2, '0')}`;
  }
};

// Function to format dates AND times based on language
export const formatDateTime = (
  date: Date | string,
  language: string = 'en'
) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) {
    console.warn(`Invalid date provided to formatDateTime: ${date}`);
    return String(date);
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short', // e.g., 'May' or 'أيار'
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true, // Use AM/PM by default, can be configured if needed
  };

  try {
    return new Intl.DateTimeFormat(language, options).format(dateObj);
  } catch (e) {
    console.error(
      `Error formatting date/time with Intl.DateTimeFormat (lang: ${language}):`,
      e
    );
    // Fallback
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return `${formatDate(
      dateObj,
      'medium',
      language
    )}, ${new Intl.DateTimeFormat(language, timeOptions).format(dateObj)}`;
  }
};

/**
 * Formats a local Libyan phone number into the standard international display format.
 * This function is now robust and can handle `+218...`, `09...`, or `9...` inputs correctly.
 * @param phoneNumber - The phone number in various formats.
 * @returns The formatted phone number (e.g., "+218 92...").
 */
export const formatLibyanPhoneNumber = (
  phoneNumber: string | null | undefined
): string => {
  if (!phoneNumber) {
    return '';
  }

  // Sanitize and extract the core number part (the part that starts with 9)
  let coreNumber = phoneNumber.trim();
  if (coreNumber.startsWith('+218')) {
    coreNumber = coreNumber.substring(4);
  } else if (coreNumber.startsWith('0')) {
    coreNumber = coreNumber.substring(1);
  }

  // At this point, coreNumber should be '9...'
  // Re-assemble into the standard display format
  return `+218 ${coreNumber}`;
};
